import { getUserConfiguration } from "owa-session-store";
import { setShowPreviewLinesOptionMutator, selectTempPreviewLinesOptionMutator, } from "../actions/selectMessageListLayoutOption";
import getShowPreviewLinesInListViewOCSSetting from "./getShowPreviewLinesInListViewOCSSetting";
import getShowPreviewLinesInListViewPrimeSetting from "./getShowPreviewLinesInListViewPrimeSetting";
import saveOcsShowPreviewLinesSetting from "./saveOcsShowPreviewLinesSetting";
import { isSingleLineListView } from "owa-mail-layout";
import { PreviewLinesOption } from "../store/schema/PreviewLinesOption";
import messageListLayoutStore from "../store/messageListLayoutStore";

let loadShowPreviewLinesInListView: Promise<void> | undefined;
export default function initializeShowPreviewLinesInListViewSetting(): Promise<void> {
    const messagePreviewStore = messageListLayoutStore();
    if (messagePreviewStore.showPreviewLinesInListView !== null) {
        if (messagePreviewStore.tempPreviewLinesInListView === null) {
            Promise.resolve().then(() => {
                if (messagePreviewStore.tempPreviewLinesInListView === null) {
                    selectTempPreviewLinesOptionMutator(messagePreviewStore.showPreviewLinesInListView);
                }
            });
        }
        return Promise.resolve();
    }
    
    // Prefer the value primed into the boot payload so the store is populated synchronously before
    // the message list first renders. This avoids a flash where a preview is briefly shown before
    // the asynchronous OCS read resolves. Falls back to the async OCS read when not primed.
    const primeShowPreviewLinesOption = getShowPreviewLinesInListViewPrimeSetting();
    if (primeShowPreviewLinesOption !== null) {
        setShowPreviewLinesOptionMutator(primeShowPreviewLinesOption);
        selectTempPreviewLinesOptionMutator(primeShowPreviewLinesOption);
        return Promise.resolve();
    }
    
    // Not primed: synchronously seed from the legacy flag to avoid a boot flash, then let the
    // async OCS read below reconcile with the authoritative value if one exists.
    const seededValue = convertPreviewTextFlagToNumber();
    setShowPreviewLinesOptionMutator(seededValue);
    
    if (!loadShowPreviewLinesInListView) {
        loadShowPreviewLinesInListView = (async () => {
            const showPreviewLinesOption = await getShowPreviewLinesInListViewOCSSetting();
            if (showPreviewLinesOption !== null) {
                // OCS has a value that was not primed this boot (set before priming shipped, or
                // the priming rollout has not reached this mailbox yet); honor it.
                setShowPreviewLinesOptionMutator(showPreviewLinesOption);
            }
            else {
                // Server is unset: persist the legacy-derived value once so future boots take the
                // prime/warm path (also primes boot data) instead of re-deriving it on every boot.
                try {
                    await saveOcsShowPreviewLinesSetting(seededValue);
                }
                catch {
                    // Best-effort: OCS stays unset and the write is retried on the next boot.
                }
            }
            
            if (messagePreviewStore.tempPreviewLinesInListView === null) {
                selectTempPreviewLinesOptionMutator(messagePreviewStore.showPreviewLinesInListView);
            }
        })().finally(() => {
            loadShowPreviewLinesInListView = undefined;
        });
    }
    return loadShowPreviewLinesInListView;
}

function convertPreviewTextFlagToNumber(): number {
    const userConfig = getUserConfiguration();
    const previewTextToNumber = userConfig?.UserOptions?.ShowPreviewTextInListView
        ? PreviewLinesOption.OneLine
        : PreviewLinesOption.Off;
    // In MCL, legacy "preview on" maps to the Inline option; "preview off" maps to "0 Lines".
    return isSingleLineListView() && previewTextToNumber === PreviewLinesOption.OneLine
        ? PreviewLinesOption.Inline
        : previewTextToNumber;
}
